import React, { useEffect, useState } from 'react'
import { Button, Grid, Hidden, Typography, useMediaQuery, Theme } from '@material-ui/core'
import { Clear as ClearIcon } from '@material-ui/icons'
import { pxToRem } from 'packages/nuggetai.ui-kit/themes/utils'
import useIntl from 'hooks/useIntl'
import SearchInput from 'components/SearchInput'
import { FilterItem } from 'components/FilterItem'
import useToggle from 'hooks/useToggle'
import FilterModal from 'components/FilterModal'
import FilterOptions from 'components/FilterOptions'
import { useTeamContext, TeamContextProps } from 'screens/Teams/Teams.config'

export interface TalentSearchProps {
	teamId: string
}

const teamSelector = (store: TeamContextProps) => ({
	handleSearch: store.handleSearch,
	refetchApplicants: store.refetchApplicants,
	debounceSearch: store.debounceSearch,
	handleChangeSort: store.handleChangeSort,
	handleToggleOrder: store.handleToggleOrder,
	resetExportMode: store.resetExportMode,
	fetchAllApplicants: store.fetchAllApplicants,
	sort: store.sort,
	talents: store.talents,
	allApplicants: store.allApplicants,
	filters: store.filters
})

export const TalentFilters: React.FC<TalentSearchProps> = ({ teamId }) => {
	const intl = useIntl()
	const [isFirst, setIsFirst] = useState(true)
	const [{ open }, { handleOpen, handleClose }] = useToggle()
	const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

	const {
		handleSearch,
		refetchApplicants,
		debounceSearch,
		handleChangeSort,
		handleToggleOrder,
		resetExportMode,
		fetchAllApplicants,
		sort,
		talents,
		allApplicants,
		filters: { onClick, clearFilters, checkoutFilter, items }
	} = useTeamContext(teamSelector)

	const sortItems = [
		{
			label: intl.formatMessage({ id: 'teams.talent.filter.score' }),
			value: 'SCORE'
		},
		{
			label: intl.formatMessage({ id: 'teams.talent.filter.createdat' }),
			value: 'CREATED_AT'
		},
		{
			label: intl.formatMessage({ id: 'teams.talent.filter.updatedat' }),
			value: 'UPDATED_AT'
		},
		{
			label: intl.formatMessage({ id: 'teams.talent.filter.name' }),
			value: 'NAME'
		},
		{
			label: intl.formatMessage({ id: 'teams.talent.filter.progress' }),
			value: 'PROGRESS'
		}
	]

	useEffect(() => {
		if (!isFirst && !mdDown) {
			refetchApplicants()
		} else {
			setIsFirst(false)
		}
	}, [JSON.stringify(checkoutFilter), debounceSearch])

	useEffect(() => {
		if (!isFirst && mdDown) {
			refetchApplicants()
		}
	}, [debounceSearch])

	return (
		<>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h4" fontWeight={500}>
						{intl.formatMessage({ id: 'teams.talent' })}
					</Typography>
				</Grid>

				<Hidden smUp>
					<Grid item xs={12}>
						<SearchInput
							fullWidth
							placeholder={intl.formatMessage({ id: 'teams.searchInputPlaceholder' })}
							onChange={handleSearch}
						/>
					</Grid>
				</Hidden>

				<Grid item xs={12}>
					<Grid container justifyContent="space-between" spacing={3}>
						<Grid item container spacing={3}>
							<Hidden smDown>
								<Grid item sx={{ width: 326 }}>
									<SearchInput
										placeholder={intl.formatMessage({ id: 'teams.searchInputPlaceholder' })}
										onChange={handleSearch}
									/>
								</Grid>
							</Hidden>

							<Grid item>
								<FilterOptions
									filters={sortItems}
									field={sort}
									handleChangeSort={handleChangeSort}
									handleToggleOrder={handleToggleOrder}
									talents={talents}
									allApplicants={allApplicants}
									fetchAllApplicants={fetchAllApplicants}
									resetExportMode={resetExportMode}
								/>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid
								container
								item
								justifyContent="space-between"
								sx={{ fontSize: pxToRem(14) }}
							>
								<Grid container item md={11}>
									<Hidden mdDown>
										{items.map(({ key, label, count }) => (
											<FilterItem
												key={key}
												onClick={onClick(key)}
												label={label}
												count={count}
											/>
										))}
									</Hidden>
									<Hidden mdUp>
										<Button sx={{ mt: 1 }} name="talentFilters" onClick={handleOpen}>
											<Typography>{intl.formatMessage({ id: 'filters' })}</Typography>
										</Button>
									</Hidden>
								</Grid>
								<Grid container item md={1} justifyContent="flex-end">
									{/* @ts-ignore */}
									{checkoutFilter.length > 0 && (
										<Hidden mdDown>
											<Button
												name="clearTalentFilters"
												onClick={clearFilters}
												color="secondary"
												size="small"
												startIcon={<ClearIcon />}
												sx={{ p: 0 }}
											>
												{intl.formatMessage({ id: 'clearAll' })}
											</Button>
										</Hidden>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<FilterModal open={open} onClose={handleClose} onOk={() => refetchApplicants()} />
		</>
	)
}

export default TalentFilters
